/**
 * Encrypts plaintext using RSA-OAEP + SHA-256 via the Web Crypto API.
 * @param publicKeyPem  SPKI PEM (-----BEGIN PUBLIC KEY-----)
 * @param plaintext     Value to encrypt (e.g. raw password)
 * @returns             Base64-encoded ciphertext
 */
export async function encryptWithPublicKey(
  publicKeyPem: string,
  plaintext: string,
): Promise<string> {
  const pemContents = publicKeyPem
    .replace('-----BEGIN PUBLIC KEY-----', '')
    .replace('-----END PUBLIC KEY-----', '')
    .replace(/\s+/g, '');

  const binaryDer = Uint8Array.from(atob(pemContents), (c) => c.charCodeAt(0));

  const cryptoKey = await window.crypto.subtle.importKey(
    'spki',
    binaryDer.buffer,
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false,
    ['encrypt'],
  );

  const encoded = new TextEncoder().encode(plaintext);
  const encrypted = await window.crypto.subtle.encrypt(
    { name: 'RSA-OAEP' },
    cryptoKey,
    encoded,
  );

  return btoa(
    Array.from(new Uint8Array(encrypted), (b) => String.fromCharCode(b)).join(''),
  );
}
